/**
 * CartLineItem Component
 * Individual line item in the cart
 */

import { useState } from 'react';
import { motion } from 'motion/react';
import type { CartLine } from '~/lib/shopify/types';
import { formatPrice, getProductUrl } from '~/lib/shopify/api';
import { useCartStore } from '~/store/cart-store';

interface CartLineItemProps {
  item: CartLine;
}

export function CartLineItem({ item }: CartLineItemProps) {
  const [isRemoving, setIsRemoving] = useState(false);

  const updateItem = useCartStore((state) => state.updateItem);
  const removeItem = useCartStore((state) => state.removeItem);
  const closeCart = useCartStore((state) => state.closeCart);

  const { merchandise, quantity, cost } = item;
  const product = merchandise.product;
  const price = merchandise.price;
  const compareAtPrice = cost.compareAtAmountPerQuantity;
  const hasDiscount = compareAtPrice && parseFloat(compareAtPrice.amount) > parseFloat(price.amount);

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity < 1) {
      handleRemove();
    } else {
      updateItem(item.id, newQuantity);
    }
  };

  const handleRemove = async () => {
    setIsRemoving(true);
    await removeItem(item.id);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: isRemoving ? 0.5 : 1, y: 0 }}
      exit={{ opacity: 0, x: 50 }}
      className="flex gap-4 bg-surface-graphite rounded-xl p-3"
    >
      {/* Image */}
      <a
        href={getProductUrl(product.handle)}
        onClick={closeCart}
        className="w-20 h-20 bg-white rounded-lg overflow-hidden flex-shrink-0"
      >
        {product.featuredImage ? (
          <img
            src={product.featuredImage.url}
            alt={product.featuredImage.altText || product.title}
            className="w-full h-full object-contain p-1"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-surface-slate">
            <svg className="w-8 h-8 text-accent-silver" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
              <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
            </svg>
          </div>
        )}
      </a>

      {/* Details */}
      <div className="flex-1 min-w-0">
        {/* Brand */}
        <span className="text-2xs text-accent-silver font-mono uppercase tracking-wider">
          {product.vendor}
        </span>

        {/* Title */}
        <a
          href={getProductUrl(product.handle)}
          onClick={closeCart}
          className="block text-sm font-medium text-accent-white hover:text-brand-orange transition-colors line-clamp-2 mt-0.5"
        >
          {product.title}
        </a>

        {/* Variant Options */}
        {merchandise.selectedOptions.length > 0 && (
          <div className="flex gap-2 mt-1">
            {merchandise.selectedOptions.map((option) => (
              <span key={option.name} className="text-2xs text-accent-silver">
                {option.value}
              </span>
            ))}
          </div>
        )}

        {/* Price & Quantity */}
        <div className="flex items-center justify-between mt-3">
          {/* Quantity */}
          <div className="quantity-selector scale-90 origin-left">
            <button
              onClick={() => handleQuantityChange(quantity - 1)}
              className="quantity-btn"
              aria-label="Decrease quantity"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            </button>
            <span className="quantity-value text-xs w-8">{quantity}</span>
            <button
              onClick={() => handleQuantityChange(quantity + 1)}
              className="quantity-btn"
              aria-label="Increase quantity"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>

          {/* Price */}
          <div className="text-right">
            <div className={`font-display font-bold ${hasDiscount ? 'text-brand-orange' : 'text-accent-white'}`}>
              {formatPrice(cost.totalAmount.amount, cost.totalAmount.currencyCode)}
            </div>
            {hasDiscount && compareAtPrice && (
              <div className="text-xs text-accent-silver line-through">
                {formatPrice(
                  (parseFloat(compareAtPrice.amount) * quantity).toString(),
                  compareAtPrice.currencyCode
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Remove Button */}
      <button
        onClick={handleRemove}
        disabled={isRemoving}
        className="self-start w-8 h-8 flex items-center justify-center text-accent-silver hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
        aria-label="Remove item"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    </motion.div>
  );
}
